import { gql, useMutation } from '@apollo/client';
import IconHide from '@apollo/icons/default/IconHide.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

const validationSchema = Yup.object().shape({
  secretName: Yup.string()
    .required('Please provide a name for your secret.')
    .matches(
      /^[a-zA-Z0-9_]*$/,
      `Secret names can only contain letters, numbers, and underscores.`,
    ),
  secretValue: Yup.string().required('Please provide a value for your secret.'),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const AddSecretModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [showSecretValue, setShowSecretValue] = useState(false);

  const graphRef = useGraphRef();

  const [saveSecret, { data: saveSecretResponseData, error }] = useMutation<
    GraphQLTypes.SaveSecretModalSetSecretMutation,
    GraphQLTypes.SaveSecretModalSetSecretMutationVariables
  >(gql`
    mutation CloudSaveSecretModalSetSecretMutation(
      $graphId: ID!
      $variant: String!
      $input: RouterSecretsInput!
    ) {
      service(id: $graphId) {
        variant(name: $variant) {
          router {
            setSecrets(input: $input) {
              ... on RouterSecretsSuccess {
                secrets {
                  createdAt
                  name
                  hash
                }
              }
              ... on InvalidInputErrors {
                message
                errors {
                  reason
                  argument
                }
              }
              ... on InternalServerError {
                message
              }
            }
          }
        }
      }
    }
  `);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleSubmit,
    resetForm,
    setFieldValue,
    setSubmitting,
    isValid,
  } = useFormik<FormValues>({
    validateOnChange: true,
    initialValues: {
      secretName: '',
      secretValue: '',
    },
    onSubmit: async ({ secretName, secretValue }) => {
      if (!graphRef) return;
      setSubmitting(true);
      const { data: responseData } = await saveSecret({
        variables: {
          graphId: graphRef.graphId,
          variant: graphRef.graphVariant,
          input: {
            secrets: [
              {
                name: secretName,
                value: secretValue,
              },
            ],
          },
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Service',
              id: graphRef.graphId,
            }),
            fieldName: 'variant',
          });
        },
      });
      if (
        responseData?.service?.variant?.router?.setSecrets?.__typename ===
        'RouterSecretsSuccess'
      ) {
        onClose();
        resetForm({});
      }
      setSubmitting(false);
    },
    validationSchema,
  });

  const userHasTouchedForm = useMemo(
    () => isMinLengthArray(1, Object.keys(touched)),
    [touched],
  );

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  const saveSecretMutationResponseHasErrors =
    saveSecretResponseData?.service?.variant?.router?.setSecrets?.__typename ===
      'InternalServerError' ||
    saveSecretResponseData?.service?.variant?.router?.setSecrets?.__typename ===
      'InvalidInputErrors' ||
    error;

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Save a new secret</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {saveSecretMutationResponseHasErrors && <ErrorMessage />}
          <FormControl isInvalid={!!errors.secretName && !!touched.secretName}>
            <FormLabel>Secret name</FormLabel>
            <Input
              onChange={(e) => setFieldValue('secretName', e.target.value)}
              value={values.secretName}
              onBlur={handleBlur}
              name="secretName"
              placeholder='Example: "TEST_SECRET"'
            />
            <FormErrorMessage>{errors.secretName}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!errors.secretValue && !!touched.secretValue}
          >
            <FormLabel>Secret value</FormLabel>
            <Input
              onChange={(e) => setFieldValue('secretValue', e.target.value)}
              value={values.secretValue}
              onBlur={handleBlur}
              name="secretValue"
              type={showSecretValue ? 'text' : 'password'}
              rightElement={
                <Tooltip
                  label={
                    showSecretValue ? 'Hide secret value' : 'Show secret value'
                  }
                >
                  <IconButton
                    aria-label={
                      showSecretValue
                        ? 'Hide secret value'
                        : 'Show secret value'
                    }
                    variant="hidden"
                    size="sm"
                    icon={<IconHide />}
                    onClick={() => setShowSecretValue(!showSecretValue)}
                  />
                </Tooltip>
              }
            />
            <FormErrorMessage>{errors.secretValue}</FormErrorMessage>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal}>
              Close
            </Button>
            <Button
              variant="primary"
              isLoading={isSubmitting}
              loadingText="Saving"
              isDisabled={!userHasTouchedForm || !isValid}
              onClick={() => handleSubmit()}
            >
              Save secret
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
